import { useState, useEffect } from "react";
import { format } from "date-fns";
import { RecordActions } from "./RecordActions";
import { RecordKeywords } from "./RecordKeywords";
import { RecordContent } from "./RecordContent";
import type { DocumentRecord } from "../../../types/record";
import { Link } from "react-router-dom";
import { Search, Hash, LinkIcon, Book, Check } from "lucide-react";
import { cn } from "../../../lib/utils";

type SortField = "id" | "created_at" | "updated_at" | "deleted_at";
type SortDirection = "asc" | "desc";

interface Reference {
  reference_type_id: number;
  reference: string;
}

interface Column {
  id: string;
  label: string;
  defaultVisible: boolean;
}

interface RecordsTableProps {
  data: DocumentRecord[];
  onUpdate: (id: string, updates: Partial<DocumentRecord>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onUnarchive: (id: number) => Promise<void>;
  onSort: (field: string, direction: SortDirection) => void;
  onSearchById: (id: string) => void;
  currentSortField?: string;
  currentSortDirection?: SortDirection;
  showArchived?: boolean;
}

const STORAGE_KEY = "recordsTableColumns";

const COLUMNS: Column[] = [
  { id: "id", label: "ID", defaultVisible: true },
  { id: "title", label: "Question", defaultVisible: true },
  { id: "content", label: "Answer", defaultVisible: true },
  { id: "references", label: "References", defaultVisible: true },
  { id: "version_count", label: "Versions", defaultVisible: false },
  { id: "created_at", label: "Created", defaultVisible: true },
  { id: "updated_at", label: "Updated", defaultVisible: true },
];

export function RecordsTable({
  data,
  onUpdate,
  onDelete,
  onUnarchive,
  onSort,
  onSearchById,
  currentSortField,
  currentSortDirection = "asc",
  showArchived,
}: RecordsTableProps) {
  const [idSearch, setIdSearch] = useState("");
  const [showIdSearch, setShowIdSearch] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return new Set(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Failed to load column preferences:', error);
    }
    return new Set(COLUMNS.filter(col => col.defaultVisible).map(col => col.id));
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(visibleColumns)));
    } catch (error) {
      console.error('Failed to save column preferences:', error);
    }
  }, [visibleColumns]);

  const formatDate = (date?: string) => {
    if (!date) return "—";
    return format(new Date(date), "MMM d, yyyy");
  };

  const handleSort = (field: SortField) => {
    const newDirection =
      field === currentSortField && currentSortDirection === "asc"
        ? "desc"
        : "asc";
    onSort(field, newDirection);
  };

  const handleIdSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && idSearch.trim()) {
      onSearchById(idSearch.trim());
    }
  };

  const toggleColumn = (columnId: string) => {
    const newVisibleColumns = new Set(visibleColumns);
    if (visibleColumns.has(columnId)) {
      newVisibleColumns.delete(columnId);
    } else {
      newVisibleColumns.add(columnId);
    }
    setVisibleColumns(newVisibleColumns);
  };

  const toggleAllColumns = () => {
    if (visibleColumns.size === COLUMNS.length) {
      setVisibleColumns(new Set());
    } else {
      setVisibleColumns(new Set(COLUMNS.map(col => col.id)));
    }
  };

  const renderReference = (ref: Reference) => {
    const isLink = ref.reference_type_id === 1;
    const preview = isLink ? (
      <a
        href={ref.reference}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:underline"
      >
        {(() => {
          try {
            const url = new URL(ref.reference);
            return url.hostname + (url.pathname.length > 20 ? url.pathname.slice(0, 20) + "..." : url.pathname);
          } catch {
            return ref.reference;
          }
        })()}
      </a>
    ) : ref.reference;

    return (
      <div
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
          isLink
            ? "bg-indigo-50 text-indigo-700"
            : "bg-emerald-50 text-emerald-700"
        }`}
      >
        {isLink ? (
          <LinkIcon className="h-3 w-3" />
        ) : (
          <Book className="h-3 w-3" />
        )}
        <span className="max-w-[200px] truncate">
          {preview}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center bg-white p-4 rounded-lg shadow-sm">
        <span className="text-sm font-medium text-gray-700">Show columns:</span>
        <button
          onClick={toggleAllColumns}
          className={cn(
            "inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition-colors",
            visibleColumns.size === COLUMNS.length
              ? "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
              : "bg-gray-50 text-gray-500 hover:bg-gray-100"
          )}
        >
          <div className={cn(
            "w-3.5 h-3.5 rounded-sm border flex items-center justify-center",
            visibleColumns.size === COLUMNS.length ? "border-indigo-500 bg-indigo-500" : "border-gray-300"
          )}>
            {visibleColumns.size === COLUMNS.length && (
              <Check className="w-2.5 h-2.5 text-white" />
            )}
          </div>
          {visibleColumns.size === COLUMNS.length ? "Hide All" : "Show All"}
        </button>
        {COLUMNS.map((column) => (
          <button
            key={column.id}
            onClick={() => toggleColumn(column.id)}
            className={cn(
              "inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition-colors",
              visibleColumns.has(column.id)
                ? "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                : "bg-gray-50 text-gray-500 hover:bg-gray-100"
            )}
          >
            <div className={cn(
              "w-3.5 h-3.5 rounded-sm border flex items-center justify-center",
              visibleColumns.has(column.id) ? "border-indigo-500 bg-indigo-500" : "border-gray-300"
            )}>
              {visibleColumns.has(column.id) && (
                <Check className="w-2.5 h-2.5 text-white" />
              )}
            </div>
            {column.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                {visibleColumns.has("id") && (
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("id")}
                  >
                    #
                  </th>
                )}
                {visibleColumns.has("title") && (
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900"
                  >
                    Question
                  </th>
                )}
                {visibleColumns.has("content") && (
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900"
                  >
                    Answer
                  </th>
                )}
                {visibleColumns.has("references") && (
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900"
                  >
                    References
                  </th>
                )}
                {visibleColumns.has("version_count") && (
                  <th
                    scope="col"
                    className="whitespace-nowrap px-6 py-3 text-left text-sm font-semibold text-gray-900"
                  >
                    Versions
                  </th>
                )}
                {showArchived && (
                  <th
                    scope="col"
                    className="whitespace-nowrap px-6 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("deleted_at")}
                  >
                    Status
                  </th>
                )}
                {visibleColumns.has("created_at") && (
                  <th
                    scope="col"
                    className="whitespace-nowrap px-6 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("created_at")}
                  >
                    Created
                  </th>
                )}
                {visibleColumns.has("updated_at") && (
                  <th
                    scope="col"
                    className="whitespace-nowrap px-6 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort("updated_at")}
                  >
                    Updated
                  </th>
                )}
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {data.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  {visibleColumns.has("id") && (
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {record.id}
                    </td>
                  )}
                  {visibleColumns.has("title") && (
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">
                        {record.title}
                      </div>
                    </td>
                  )}
                  {visibleColumns.has("content") && (
                    <td className="px-6 py-4">
                      <RecordContent
                        content={record.content}
                        formattedContent={record.formatted_content}
                      />
                    </td>
                  )}
                  {visibleColumns.has("references") && (
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1.5">
                        {record.references?.map((ref, index) => (
                          <div key={index}>
                            {renderReference(ref)}
                          </div>
                        ))}
                      </div>
                    </td>
                  )}
                  {visibleColumns.has("version_count") && (
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                        {record.version_count || 1}
                      </span>
                    </td>
                  )}
                  {showArchived && (
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      {record.deleted_at ? (
                        <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
                          Archived
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                          Active
                        </span>
                      )}
                    </td>
                  )}
                  {visibleColumns.has("created_at") && (
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {formatDate(record.created_at)}
                    </td>
                  )}
                  {visibleColumns.has("updated_at") && (
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {formatDate(record.updated_at)}
                    </td>
                  )}
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    <RecordActions
                      id={record.id}
                      onDelete={() => onDelete(record.id)}
                      onUnarchive={() => onUnarchive(Number(record.id))}
                      onPreview={() =>
                        window.open(`/preview/${record.id}`, "_blank")
                      }
                      isArchived={!!record.deleted_at}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
